// Settings page for activating/deactivating business modules. Same layout
// as the onboarding wizard's step 3 (toggleable cards) but persists every
// click immediately via the useUpdateModuleSetting mutation. The sidebar
// re-renders the moment the optimistic cache update lands, so users see
// groups appear/disappear without a page reload.

import { useTranslation } from 'react-i18next';
import { Badge, Card, CardContent, PageHeader } from '@erp/ui';
import {
  Check,
  ShoppingCart, ShoppingBag, Warehouse, Banknote, Landmark,
  BookCheck, FileText, Receipt,
} from '@/components/icons';
import { useEnabledModules, useUpdateModuleSetting } from '@/hooks/use-modules';
import type { ModuleKey } from '@erp/data-access/interfaces';

interface ModuleUiEntry {
  key: ModuleKey;
  icon: React.ReactNode;
  title: string;
  desc: string;
  alwaysOn?: boolean;
}

// Same catalog as the wizard's Step 3, kept duplicated rather than
// shared because the wizard imports icon JSX directly and so does this
// page — extracting to a shared module would re-introduce the icon
// indirection (looking up by string) that we explicitly avoided in
// libs/data-access/interfaces/src/tenant-onboarding.ts.
const MODULE_UI: ModuleUiEntry[] = [
  {
    key: 'sales',
    icon: <ShoppingCart size={22} />,
    title: 'Ventes',
    desc: 'Devis, commandes, BL, factures, avoirs. Cœur du logiciel.',
    alwaysOn: true,
  },
  {
    key: 'purchases',
    icon: <ShoppingBag size={22} />,
    title: 'Achats',
    desc: 'Commandes fournisseurs, bons de réception, factures d\u2019achat.',
  },
  {
    key: 'stock',
    icon: <Warehouse size={22} />,
    title: 'Stock & inventaires',
    desc: 'Suivi des quantités, mouvements, dépôts, inventaires.',
  },
  {
    key: 'payments',
    icon: <Banknote size={22} />,
    title: 'Paiements',
    desc: 'Encaissements et décaissements, paiements récurrents.',
  },
  {
    key: 'treasury',
    icon: <Landmark size={22} />,
    title: 'Trésorerie',
    desc: 'Comptes bancaires, transactions, soldes consolidés.',
  },
  {
    key: 'checks',
    icon: <BookCheck size={22} />,
    title: 'Chèques & Traites',
    desc: 'Suivi des chèques émis/reçus et des traites.',
  },
  {
    key: 'withholding',
    icon: <FileText size={22} />,
    title: 'Retenue à la source',
    desc: 'Calcul automatique RAS et certificats légaux (Tunisie).',
  },
  {
    key: 'storefront',
    icon: <ShoppingBag size={22} />,
    title: 'Boutique en ligne',
    desc: 'Site e-commerce sur votre sous-domaine .gesty.tn.',
  },
  {
    key: 'caisse',
    icon: <Receipt size={22} />,
    title: 'Caisse / POS',
    desc: 'Point de vente en magasin, sessions, mouvements de caisse.',
  },
];

export function ModulesSettingsPage() {
  const { t } = useTranslation();
  const { enabled, isLoading } = useEnabledModules();
  const update = useUpdateModuleSetting();

  const handleToggle = (key: ModuleKey, alwaysOn: boolean | undefined, isOn: boolean) => {
    if (alwaysOn) return;
    update.mutate({ moduleKey: key, isEnabled: !isOn });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('modulesSettings.title', { defaultValue: 'Modules' })}
        subtitle={t('modulesSettings.subtitle', {
          defaultValue:
            "Activez ou désactivez les modules selon vos besoins. Les modules désactivés disparaissent de la barre latérale mais leurs données restent intactes — vous pouvez les réactiver à tout moment.",
        })}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {MODULE_UI.map((m) => {
          const isOn = enabled.has(m.key) || !!m.alwaysOn;
          return (
            // No primary-tinted accent border — the toggle and the
            // tinted icon tile already communicate the active state
            // clearly enough without a loud blue outline around every
            // enabled card.
            <Card key={m.key}>
              <CardContent className="p-4 flex items-start gap-3">
                <div
                  className={[
                    'mt-0.5 size-10 shrink-0 rounded-md grid place-items-center',
                    isOn
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground',
                  ].join(' ')}
                >
                  {m.icon}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-medium">{m.title}</h3>
                    {m.alwaysOn ? (
                      <Badge variant="secondary" className="text-[10px] shrink-0">
                        Toujours actif
                      </Badge>
                    ) : (
                      <ToggleSwitch
                        checked={isOn}
                        disabled={isLoading || update.isPending}
                        onChange={() => handleToggle(m.key, m.alwaysOn, isOn)}
                        ariaLabel={m.title}
                      />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{m.desc}</p>
                  {isOn && !m.alwaysOn ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-primary mt-2">
                      <Check size={12} /> Activé
                    </span>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {update.isError ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
          La modification a échoué. Réessayez.
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Inline switch — libs/ui doesn't ship a Switch yet, so we keep */
/*  this small and local rather than dragging a Radix dep into    */
/*  the shared package just for one toggle.                        */
/* -------------------------------------------------------------- */

interface ToggleSwitchProps {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  ariaLabel?: string;
}

function ToggleSwitch({ checked, disabled, onChange, ariaLabel }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) onChange();
      }}
      className={[
        // h-6 / w-11 = a touch larger than the previous 5/9 — easier to
        // hit on mobile and matches platform-native switches.
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full',
        'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        disabled ? 'cursor-not-allowed opacity-60' : '',
        // Off-state track: bright enough to be seen against the dark
        // background. The previous `bg-muted-foreground/30` rendered as
        // a near-invisible 30 %-opacity grey on `--background`.
        checked ? 'bg-primary' : 'bg-foreground/25 dark:bg-foreground/30',
      ].join(' ')}
    >
      <span
        aria-hidden
        className={[
          // Pure white thumb so it stays visible regardless of theme —
          // `bg-background` was dark-on-dark in dark mode and made the
          // thumb disappear into the track.
          'pointer-events-none inline-block size-5 rounded-full bg-white shadow-md ring-0 transform transition-transform',
          checked ? 'translate-x-[22px] rtl:-translate-x-[22px]' : 'translate-x-0.5 rtl:-translate-x-0.5',
        ].join(' ')}
      />
    </button>
  );
}
